import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import useDocumentMeta from '@/hooks/useDocumentMeta';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const [input, setInput] = useState(query);

  useDocumentMeta({
    title: query ? `“${query}”的搜索结果 - 妖精视频` : '搜索影视内容 - 妖精视频',
    description: query
      ? `妖精视频关于“${query}”的搜索结果，为你匹配相关电影、电视剧与综艺内容。`
      : '在妖精视频搜索电影、电视剧、综艺等影视内容，快速找到你想看的作品。',
    keywords: '妖精视频,影视搜索,电影搜索,电视剧搜索,综艺搜索',
  });

  const results = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return movies.filter((m) =>
      [m.title, m.region, m.director, ...m.actors, ...m.genres, ...m.tags]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [query]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 font-medium">搜索</span>
        </nav>

        <div className="mt-5 rounded-lg bg-primary-900 px-6 md:px-10 py-8">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-background-50">
            {query ? `“${query}”的搜索结果` : '搜索影视内容'}
          </h1>
          <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影片名称、演员或关键词"
                className="w-full rounded-md border border-transparent bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-accent-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-accent-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8">
        {query && (
          <p className="text-sm text-foreground-600 mb-5">
            找到 <span className="font-semibold text-primary-600">{results.length}</span> 条与“{query}”相关的影视内容
          </p>
        )}

        {query && results.length > 0 && (
          <div className="flex flex-col gap-4">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} variant="list" />
            ))}
          </div>
        )}

        {query && results.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
              <i className="ri-search-eye-line"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">
              没有找到与“{query}”相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或从下面的热门分类开始浏览</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/category/movie" className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">电影频道</Link>
              <Link to="/category/tv" className="whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600">电视剧频道</Link>
              <Link to="/category/variety" className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600">综艺频道</Link>
            </div>
          </div>
        )}

        {!query && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">开始你的影视发现之旅</h2>
            <p className="mt-2 text-sm text-foreground-600">输入片名、演员或类型关键词，快速找到想看的作品</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/category/movie" className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600">浏览电影</Link>
              <Link to="/category/tv" className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600">浏览电视剧</Link>
              <Link to="/category/variety" className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600">浏览综艺</Link>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}